import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Trash2, Plus, Minus, ArrowRight, ShoppingBag, ShieldCheck, Truck, Gift, AlertCircle } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/Button';
import { PageTransition } from '../components/common/PageTransition';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
export function CartPage() {
  const {
    items,
    removeFromCart,
    updateQuantity,
    getCartTotal
  } = useCart();
  const navigate = useNavigate();
  const total = getCartTotal();
  const shippingThreshold = 49;
  const remainingForFreeShipping = Math.max(0, shippingThreshold - total);
  const shippingCost = remainingForFreeShipping > 0 ? 4.95 : 0;
  const finalTotal = total + shippingCost;
  if (items.length === 0) {
    return <PageTransition data-id="element-5786">
        <div className="min-h-[80vh] flex flex-col items-center justify-center bg-[#FAF9F6] px-4" data-id="element-5787">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6" data-id="element-5788">
            <ShoppingBag className="w-10 h-10 text-gray-400" data-id="element-5789" />
          </div>
          <h1 className="text-3xl font-bold font-serif text-charcoal-900 mb-4" data-id="element-5790">
            Tu carrito está vacío
          </h1>
          <p className="text-charcoal-600 mb-8 text-center max-w-md" data-id="element-5791">
            Parece que aún no has añadido nada. ¡Descubre nuestros menús frescos
            y naturales para tu mascota!
          </p>
          <Link to="/productos" data-id="element-5792">
            <Button size="lg" className="font-bold px-8" data-id="element-5793">
              Explorar Menú
            </Button>
          </Link>
        </div>
      </PageTransition>;
  }
  return <PageTransition data-id="element-5794">
      <div className="bg-[#FAF9F6] min-h-screen py-12 lg:py-20" data-id="element-5795">
        <div className="container-custom max-w-7xl mx-auto" data-id="element-5796">
          <h1 className="text-4xl font-bold font-serif text-charcoal-900 mb-8" data-id="element-5797">
            Tu Carrito ({items.length} productos)
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-5798">
            {/* Cart Items List */}
            <div className="lg:col-span-2 space-y-6" data-id="element-5799">
              {/* Free Shipping Progress */}
              <Card className="bg-primary-50 border-primary-100 p-4" data-id="element-5800">
                <div className="flex items-center gap-3 mb-2" data-id="element-5801">
                  <Truck className="w-5 h-5 text-primary-600" data-id="element-5802" />
                  <p className="text-sm font-bold text-primary-800" data-id="element-5803">
                    {remainingForFreeShipping > 0 ? `¡Te faltan ${remainingForFreeShipping.toFixed(2)}€ para envío GRATIS!` : '¡Genial! Tienes envío GRATIS'}
                  </p>
                </div>
                <div className="h-2 bg-primary-200 rounded-full overflow-hidden" data-id="element-5804">
                  <motion.div initial={{
                  width: 0
                }} animate={{
                  width: `${Math.min(100, total / shippingThreshold * 100)}%`
                }} className="h-full bg-primary-600 rounded-full" data-id="element-5805" />
                </div>
              </Card>

              <AnimatePresence data-id="element-5806">
                {items.map(item => <motion.div key={item.product.id} layout initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                x: -100
              }} className="bg-white rounded-2xl p-4 sm:p-6 border border-gray-100 shadow-sm flex gap-6 items-center" data-id="element-5807">
                    <div className="w-24 h-24 rounded-xl overflow-hidden bg-gray-50 border border-gray-100 shrink-0" data-id="element-5808">
                      <img src={item.product.image} alt={item.product.name} className="w-full h-full object-cover" data-id="element-5809" />
                    </div>

                    <div className="flex-1 min-w-0" data-id="element-5810">
                      <div className="flex justify-between items-start mb-2" data-id="element-5811">
                        <div data-id="element-5812">
                          <h3 className="font-bold text-charcoal-900 text-lg truncate pr-4" data-id="element-5813">
                            {item.product.name}
                          </h3>
                          <p className="text-sm text-charcoal-500" data-id="element-5814">
                            {item.product.category === 'dog-food' ? 'Comida Perro' : 'Comida Gato'}
                          </p>
                        </div>
                        <button onClick={() => removeFromCart(item.product.id)} className="text-gray-400 hover:text-rose-500 transition-colors p-1" aria-label="Eliminar" data-id="element-5815">
                          <Trash2 className="w-5 h-5" data-id="element-5816" />
                        </button>
                      </div>

                      <div className="flex justify-between items-end mt-4" data-id="element-5817">
                        <div className="flex items-center bg-gray-50 rounded-lg border border-gray-200 h-10" data-id="element-5818">
                          <button onClick={() => updateQuantity(item.product.id, Math.max(1, item.quantity - 1))} className="w-10 h-full flex items-center justify-center text-gray-500 hover:text-primary-600 transition-colors" data-id="element-5819">
                            <Minus className="w-4 h-4" data-id="element-5820" />
                          </button>
                          <span className="w-8 text-center font-bold text-sm" data-id="element-5821">
                            {item.quantity}
                          </span>
                          <button onClick={() => updateQuantity(item.product.id, item.quantity + 1)} className="w-10 h-full flex items-center justify-center text-gray-500 hover:text-primary-600 transition-colors" data-id="element-5822">
                            <Plus className="w-4 h-4" data-id="element-5823" />
                          </button>
                        </div>
                        <div className="text-right" data-id="element-5824">
                          <p className="font-bold text-xl text-charcoal-900" data-id="element-5825">
                            {(item.product.price * item.quantity).toFixed(2)}€
                          </p>
                          {item.quantity > 1 && <p className="text-xs text-gray-500" data-id="element-5826">
                              {item.product.price.toFixed(2)}€ / ud
                            </p>}
                        </div>
                      </div>
                    </div>
                  </motion.div>)}
              </AnimatePresence>

              {/* Upsell / Recommended */}
              <div className="mt-12" data-id="element-5827">
                <h3 className="font-bold text-xl mb-6 font-serif" data-id="element-5828">
                  Completa tu pedido
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-5829">
                  {/* Mock Upsell Items */}
                  {[1, 2].map(i => <div key={i} className="bg-white p-4 rounded-xl border border-gray-100 flex gap-4 items-center hover:border-primary-200 transition-colors cursor-pointer group" data-id="element-5830">
                      <div className="w-16 h-16 rounded-lg bg-gray-50 overflow-hidden" data-id="element-5831">
                        <img src={`https://images.unsplash.com/photo-${i === 1 ? '1583337130417-3346a1be7dee' : '1587593810167-a84920ea0781'}?auto=format&fit=crop&q=80&w=200`} alt="Snack" className="w-full h-full object-cover" data-id="element-5832" />
                      </div>
                      <div className="flex-1" data-id="element-5833">
                        <h4 className="font-bold text-sm mb-1" data-id="element-5834">
                          Snacks Naturales
                        </h4>
                        <p className="text-primary-700 font-bold text-sm" data-id="element-5835">
                          +4.95€
                        </p>
                      </div>
                      <button className="w-8 h-8 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center group-hover:bg-primary-600 group-hover:text-white transition-colors" data-id="element-5836">
                        <Plus className="w-4 h-4" data-id="element-5837" />
                      </button>
                    </div>)}
                </div>
              </div>
            </div>

            {/* Order Summary Sidebar */}
            <div className="lg:col-span-1" data-id="element-5838">
              <div className="sticky top-24 space-y-6" data-id="element-5839">
                <Card className="p-6 sm:p-8 shadow-soft-xl border-gray-100" data-id="element-5840">
                  <h2 className="text-xl font-bold font-serif mb-6" data-id="element-5841">Resumen</h2>

                  <div className="space-y-4 mb-6 text-sm" data-id="element-5842">
                    <div className="flex justify-between text-charcoal-600" data-id="element-5843">
                      <span data-id="element-5844">Subtotal</span>
                      <span data-id="element-5845">{total.toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between text-charcoal-600" data-id="element-5846">
                      <span data-id="element-5847">Envío</span>
                      {shippingCost === 0 ? <span className="text-emerald-600 font-bold" data-id="element-5848">
                          GRATIS
                        </span> : <span data-id="element-5849">{shippingCost.toFixed(2)}€</span>}
                    </div>
                    <div className="pt-4 border-t border-gray-100 flex justify-between items-end" data-id="element-5850">
                      <span className="font-bold text-lg text-charcoal-900" data-id="element-5851">
                        Total
                      </span>
                      <div className="text-right" data-id="element-5852">
                        <span className="block font-bold text-2xl text-primary-800" data-id="element-5853">
                          {finalTotal.toFixed(2)}€
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-5854">
                          IVA incluido
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Promo Code */}
                  <div className="mb-6" data-id="element-5855">
                    <div className="flex gap-2" data-id="element-5856">
                      <Input placeholder="Código promocional" className="bg-gray-50 border-gray-200 text-sm" data-id="element-5857" />
                      <Button variant="outline" size="sm" data-id="element-5858">
                        Aplicar
                      </Button>
                    </div>
                  </div>

                  <Button fullWidth size="xl" className="font-bold shadow-xl shadow-primary-900/20 mb-4" onClick={() => navigate('/checkout')} data-id="element-5859">
                    Tramitar Pedido
                    <ArrowRight className="ml-2 w-5 h-5" data-id="element-5860" />
                  </Button>

                  <div className="flex items-center justify-center gap-2 text-xs text-gray-500" data-id="element-5861">
                    <ShieldCheck className="w-4 h-4 text-emerald-500" data-id="element-5862" />
                    <span data-id="element-5863">Pago 100% Seguro</span>
                  </div>
                </Card>

                {/* Trust Badges */}
                <div className="grid grid-cols-2 gap-4" data-id="element-5864">
                  <div className="bg-white p-4 rounded-xl border border-gray-100 text-center" data-id="element-5865">
                    <Gift className="w-6 h-6 text-primary-500 mx-auto mb-2" data-id="element-5866" />
                    <p className="text-xs font-bold text-charcoal-600" data-id="element-5867">
                      Regalo Sorpresa
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-xl border border-gray-100 text-center" data-id="element-5868">
                    <AlertCircle className="w-6 h-6 text-primary-500 mx-auto mb-2" data-id="element-5869" />
                    <p className="text-xs font-bold text-charcoal-600" data-id="element-5870">
                      Soporte 24/7
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageTransition>;
}